import { useLayoutEffect, type ReactNode } from 'react';
import { LoginCard, useRoute, type ModuleScreenContext } from '@huaca/ui';
import { TendersWordmark } from './TendersMark';
import { FlatPrice, ManyIntoOne, OneBeer, PulledApart, ShipsWeekly, SourceRail } from './landing-art';
import { BUILDING, CO_LEAD_AFTER, CO_LEAD_BEFORE, CO_LEAD_STRONG, CO_NOTE, SHIPPED } from './landing-copy';
import { LandingDemo } from './landing-demo';
import { buildGlyph } from './landing-glyphs';
import { CONTACT_EMAIL, ContactPage, contactReady } from './landing-contact';
import { GateChapters, GateRail } from './landing-how';
import { STORY, StoryPage, storyReady } from './landing-story';
import { paidPlans, splitPrice, trialPlan, useLandingFacts, type LandingFacts } from './landing-facts';
import { revealStep, useReveal, type RevealStep } from './landing-reveal';
import './landing.css';

/**
 * The tenders landing page — this module's `renderLogin`.
 *
 * WHY A LANDING PAGE IS A LOGIN SCREEN. `ModuleApp` decides WHEN each screen shows and lets a module
 * decide what it LOOKS like; the anonymous phase is the only surface this product has before a
 * session exists, so the marketing page and the login page are necessarily the same screen. That is
 * also the constraint everything here is built around: whatever else fails, the form must render.
 * No copy, no figure and no illustration on this page may sit between a visitor and signing in.
 *
 * It composes `<LoginCard>` rather than reimplementing it, exactly as the platform intends — the
 * card carries Google SSO and the magic-link flow, and both are origin-bound to this subdomain.
 *
 * NOTHING HERE STATES A NUMBER OF ITS OWN. The price and the trial come from `/plans`, the sources
 * and the counts from `/api/tenders/overview`; a sentence that needs a figure is simply not written
 * when the figure has not arrived. The one exception is the company section, kept verbatim on
 * instruction — see landing-copy.tsx, which says so at the top.
 */

/**
 * The bands, in order.
 *
 * THE GROUND ALTERNATES BY POSITION rather than being written onto each band: light, deep, light,
 * deep… So the rhythm is a property of the list and cannot drift as bands are added, reordered or
 * removed — the failure mode with six hand-set classes is two deep bands ending up adjacent and
 * nobody noticing until it is on staging.
 */
export function isDeepBand(index: number): boolean {
  return index % 2 === 1;
}

/** The class marking the document while the landing is mounted — see landing.css for what it undoes. */
export const LANDING_ROOT_CLASS = 'tenders-landing-open';

/**
 * The sign-in region's id, and the target of every call to action on the page.
 *
 * EVERY ROUTE TO THE FORM IS A PLAIN `<a href="#…">`, deliberately — no click handler, no scroll
 * script. A native fragment link scrolls AND moves focus, works with JavaScript disabled, and honours
 * `prefers-reduced-motion` through CSS rather than through a branch someone has to remember to write.
 * On this page in particular that matters: it is the login screen, so a scroll handler that failed to
 * run would leave a returning visitor pressing a button that does nothing.
 */
export const LOGIN_ID = 'tenders-login';

/** An LTR-isolated numeral island — the design system's own, so digits sit correctly in Hebrew. */
function Num({ children }: { children: ReactNode }) {
  return <span className="huaca-num">{children}</span>;
}

/**
 * A price label, set so only its NUMERAL is direction-isolated.
 *
 * `.huaca-num` forces `direction: ltr`, which is right for "₪2" and wrong for "₪2 לחודש" — the whole
 * label is a mixed run, and isolating it flips the Hebrew word order, rendering "₪3 לחודש" on screen
 * as "לחודש 3₪". Found by looking at the page, not by a test: jsdom lays nothing out and reports the
 * DOM order, which was correct all along.
 */
function Price({ label }: { label: string }) {
  const { amount, unit } = splitPrice(label);
  return (
    <>
      <Num>{amount}</Num>
      {unit ? ` ${unit}` : ''}
    </>
  );
}

function Cta({ children }: { children: ReactNode }) {
  return (
    <a className="tenders-landing-cta" href={`#${LOGIN_ID}`}>
      {children}
    </a>
  );
}

/**
 * The no-card trial, in the server's own words.
 *
 * `display.priceLabel` on the trial plan already reads "חינם ל-3 ימים", so the length of the trial is
 * never restated here and cannot disagree with `trialDays` in plans.ts the way a sentence of our own
 * would. Absent plans, the line is absent.
 */
function TrialLine({ facts }: { facts: LandingFacts }) {
  const trial = trialPlan(facts.plans);
  if (!trial) return null;
  return <p className="tenders-landing-micro">{trial.display.priceLabel}. בלי כרטיס אשראי.</p>;
}

interface BandProps {
  ctx: ModuleScreenContext;
  facts: LandingFacts;
}

/** The id of the summary section on the landing, so it can be linked to directly. */
export const HOW_ID = 'tenders-how';

/**
 * The page that explains the pipeline at length.
 *
 * A path this module serves itself. Nothing registers it: ModuleApp routes every anonymous path to
 * `renderLogin`, and firebase.json rewrites `**` to index.html, so a deep link and a reload both
 * land here without a server route or a platform change.
 */
export const HOW_PATH = '/how-it-works';

/** The two pages that are built and waiting on copy — see landing-story.tsx and landing-contact.tsx. */
export const STORY_PATH = '/story';
export const CONTACT_PATH = '/contact';

interface Band {
  id: string;
  /** The first band is the page's masthead; the rest are sections. */
  masthead?: boolean;
  /**
   * Exempt from the scroll reveal.
   *
   * Set on the band that holds the sign-in form, and it is not a stylistic preference: a visitor who
   * follows a `#tenders-login` link, or lands on one from a bookmark, arrives at the form directly.
   * A form that fades in — for 550ms, or for ever if the observer misfires — is the one thing on
   * this page that must never depend on an animation having run.
   */
  noReveal?: boolean;
  /** This band's drawing. Decorative — see landing-art.tsx. */
  art: (props: { className?: string }) => ReactNode;
  /**
   * Where the drawing sits. Omitted, it goes beside the words (under them on a narrow viewport);
   * `strip` runs it the width of the band, which only the source rail wants — it prefigures the list
   * of publishers that follows it rather than illustrating the heading.
   */
  artLayout?: 'strip';
  /**
   * Rendered full-width ABOVE the heading row, in its own reveal unit.
   *
   * One band uses it, and only because the alternation is load-bearing: "איך זה עובד" is a section
   * of its own, but adding a seventh band would flip every ground after it and end the page on the
   * wrong one. It belongs in this band anyway — the pipeline and the worked example are the same
   * claim, stated twice.
   */
  above?: (p: BandProps) => ReactNode;
  render: (p: BandProps) => ReactNode;
  /**
   * Rendered full-width BENEATH the heading row. For everything that is not prose — the source list,
   * the worked example, the two company lists — which need the width and must not compete with a
   * drawing for it.
   */
  below?: (p: BandProps) => ReactNode;
}

const BANDS: Band[] = [
  {
    id: 'hero',
    masthead: true,
    art: ManyIntoOne,
    render: ({ facts }) => (
      <>
        <h1>סוף־סוף. כל המכרזים והמשרות במקום אחד.</h1>
        <p className="tenders-landing-lede">
          עיריות, משרדי ממשלה, חברות ממשלתיות ותאגידים — נסרקים כל בוקר לתוך לוח אחד. את רובם לא פתחת
          אף פעם, ושם בדיוק יש הכי פחות מתחרים.
        </p>
        <Cta>תפתח את הלוח</Cta>
        <TrialLine facts={facts} />
      </>
    ),
  },
  {
    id: 'sources',
    art: SourceRail,
    artLayout: 'strip',
    render: ({ facts }) => {
      const sources = facts.overview?.sources;
      return (
        <>
          <h2>כל המקורות</h2>
          {/*
            `sources === null` means the corpus could not be read, which is NOT "we cover nobody" —
            so the count is written only when there is one, and never as a zero.
          */}
          {sources ? (
            <p className="tenders-landing-lede">
              <Num>{sources.length}</Num> מקורות נסרקים כל בוקר. כל מקור שנוסף מגיע לכל המנויים באותו
              יום.
            </p>
          ) : (
            <p className="tenders-landing-lede">כל מקור שנוסף מגיע לכל המנויים באותו יום.</p>
          )}
        </>
      );
    },
    below: ({ facts }) => {
      const sources = facts.overview?.sources;
      if (!sources?.length) return null;
      return (
        <ul className="tenders-sources" aria-label="רשימת המקורות">
          {sources.map((s) => (
            <li key={s.publisher} className="tenders-sources__item">
              {s.publisher}
            </li>
          ))}
        </ul>
      );
    },
  },
  {
    id: 'price',
    art: OneBeer,
    render: ({ facts }) => {
      const paid = paidPlans(facts.plans);
      const lead = paid[0];
      const rest = paid.slice(1);
      if (!lead) {
        return (
          <>
            <h2>המחיר.</h2>
            <Cta>תפתח את הלוח</Cta>
            <TrialLine facts={facts} />
          </>
        );
      }
      const { amount, unit } = splitPrice(lead.display.priceLabel);
      return (
        <>
          <h2>המחיר.</h2>
          <p className="tenders-price">
            <Num>{amount}</Num>
            {unit ? <small>{unit}</small> : null}
          </p>
          <p className="tenders-price__beer">פחות מבירה אחת.</p>
          <div className="tenders-price__notes">
            <p>
              <b>עבודה אחת שתמצא כאן שווה יותר מכל מה שתשלם לנו — ומכל מה שתשלם לנו בעשור הבא.</b>
            </p>
            {/*
              The tiers are LISTED rather than denied. The design this came from said "no tiers, no
              call-us-for-pricing"; there are two, so the page names the second in the server's own
              words instead of claiming it away.
            */}
            {rest.length ? (
              <p>
                למי שעוקב כל יום יש גם{' '}
                {rest.map((plan, i) => (
                  <span key={plan.id}>
                    {i > 0 ? ' · ' : ''}
                    {plan.display.name} — <Price label={plan.display.priceLabel} />
                  </span>
                ))}
                . אותן היכולות, יותר נפח.
              </p>
            ) : null}
            <p>הלוח גדל כל חודש. המחיר לא.</p>
          </div>
          <Cta>תפתח את הלוח</Cta>
          <TrialLine facts={facts} />
        </>
      );
    },
  },
  {
    id: 'anatomy',
    art: PulledApart,
    above: ({ ctx, facts }) => (
      <div className="tenders-pipeline" id={HOW_ID}>
        <p className="tenders-landing-eyebrow">איך זה עובד</p>
        <h2>ארבעה שערים, בין הפרסום לבינך.</h2>
        <p className="tenders-landing-lede">
          מכרז הוא רצף מועדים, לא תאריך אחד — וזה גם מה שקורה לו אצלנו בדרך אליך.
        </p>
        {/* The SAME four gates the full page renders, one sentence each. Defined once in
            landing-how.tsx, because a summary and an explanation that disagree about what the
            product does would both read fine on their own. */}
        <GateRail overview={facts.overview} />
        <p className="tenders-pipeline__more">
          <PageLink to={HOW_PATH} ctx={ctx}>
            לקריאה המלאה על התהליך ←
          </PageLink>
        </p>
      </div>
    ),
    render: ({ facts }) => {
      const o = facts.overview;
      return (
        <>
          <h2>ככה נראה מכרז אצלנו.</h2>
          <p className="tenders-landing-lede">
            {o?.postings ? (
              <>
                <Num>{o.postings.tender.toLocaleString('he-IL')}</Num> מכרזים ו-
                <Num>{o.postings.job.toLocaleString('he-IL')}</Num> משרות פתוחים כרגע —{' '}
              </>
            ) : null}
            {o ? (
              <>
                כל אחד מפורק ל-<Num>{o.fields}</Num> שדות לפני שראית אותו.
              </>
            ) : (
              'כל פרסום מפורק לשדות לפני שראית אותו.'
            )}
          </p>
        </>
      );
    },
    below: ({ facts }) => (
      <>
        <LandingDemo />
        {facts.overview ? (
          <p className="tenders-landing-note">
            <Num>{facts.overview.filters}</Num> מסננים, בחירה מרובה בכל אחד, ומיון לפי כל עמודה. אותה
            רשימה גם ככרטיסים, אם ככה נוח לך לקרוא.
          </p>
        ) : null}
      </>
    ),
  },
  {
    id: 'company',
    art: ShipsWeekly,
    render: () => (
      <>
        <p className="tenders-landing-eyebrow">מי בונה את זה</p>
        <h2>חברה קטנה. קצב מהיר. סוכנים שעושים את העבודה.</h2>
        <p className="tenders-co__lead">
          {CO_LEAD_BEFORE}
          <b>{CO_LEAD_STRONG}</b>
          {CO_LEAD_AFTER}
        </p>
      </>
    ),
    below: () => (
      <>
        {/*
          Cards, where this was a stack. NOT ONE WORD CHANGES — `BUILDING` is rendered exactly as it
          is written, and landing-copy.tsx is untouched. What changes is that the two states the copy
          already distinguishes ("רץ בבדיקות" against "בעבודה") are now drawn as two states: the
          closing note spends a sentence explaining a difference the old layout never showed.
        */}
        <div className="tenders-co__now">
          <p className="tenders-co__pill">מה אנחנו בונים עכשיו</p>
          <div className="tenders-builds">
            {BUILDING.map((b) => (
              <div key={b.title} className={`tenders-build${b.soon ? ' tenders-build--soon' : ''}`}>
                <div className="tenders-build__head">
                  {/* No tile at all when nothing has been drawn for this title — plainly missing
                      beats a stand-in mark that says something the card does not. */}
                  <span className="tenders-build__icon">{buildGlyph(b.title)}</span>
                  <span className="tenders-build__tag">
                    <i className="tenders-build__dot" aria-hidden="true" />
                    {b.tag}
                  </span>
                </div>
                <h3>{b.title}</h3>
                <p>{b.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="tenders-co__shipped">
          <p className="tenders-landing-sublabel">מה כבר נשלח</p>
          {SHIPPED.map((s) => (
            <p key={s.date} className="tenders-ship">
              <b className="huaca-num">{s.date}</b>
              <span>{s.what}</span>
            </p>
          ))}
        </div>

        <p className="tenders-landing-note">{CO_NOTE}</p>
      </>
    ),
  },
  {
    id: 'close',
    art: FlatPrice,
    noReveal: true,
    render: ({ ctx, facts }) => {
      const sources = facts.overview?.sources;
      const lead = paidPlans(facts.plans)[0];
      return (
        <>
          <h2>הלוח ימשיך לגדול. המחיר לא.</h2>
          {sources && lead ? (
            <p className="tenders-landing-lede">
              היום <Num>{sources.length}</Num> מקורות ב־<Price label={lead.display.priceLabel} />.
              כשיהיו מאה, זה עדיין יהיה <Price label={lead.display.priceLabel} />.
            </p>
          ) : null}
          {/*
            `tabIndex={-1}` is what makes the fragment link an ACCESSIBILITY affordance rather than
            just a scroll: without it the browser moves the viewport but leaves focus at the top of
            the document, so a keyboard visitor arrives looking at the form and tabbing through the
            whole page to reach it.
          */}
          <div className="tenders-landing-login tenders-lightpin" id={LOGIN_ID} tabIndex={-1}>
            <LoginCard productName={ctx.productName} />
          </div>
        </>
      );
    },
  },
];

/**
 * A link to another page of this landing.
 *
 * A REAL `href` with a handler layered on top, not a button pretending to be a link. So it opens in
 * a new tab on middle-click, copies as a URL, and — the case that matters here — still navigates
 * with no JavaScript at all, because firebase.json rewrites every path to index.html. The handler
 * only saves a full reload; it is never what makes the link work.
 */
function PageLink({
  to,
  ctx,
  current,
  children,
}: {
  to: string;
  ctx: ModuleScreenContext;
  /** Marks the link to the page you are already on. Belongs on the anchor, not on a wrapper. */
  current?: boolean;
  children: ReactNode;
}) {
  return (
    <a
      href={to}
      aria-current={current ? 'page' : undefined}
      onClick={(event) => {
        // Everything the browser does better than we do: new tab, new window, download, middle-click.
        if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (event.button !== 0) return;
        event.preventDefault();
        ctx.navigate(to);
        // Otherwise the new page opens at the old page's scroll position, part way down.
        window.scrollTo(0, 0);
      }}
    >
      {children}
    </a>
  );
}

/**
 * The masthead and footer, shared by every page this component serves.
 *
 * The sign-in link is the reason this is one component rather than two copies: on the landing the
 * form is on the page, so the link is a fragment; anywhere else it has to be a path, or it points at
 * nothing and the one control this page may never break is broken.
 */
/**
 * Every page this module serves besides the landing itself.
 *
 * ONE LIST DRIVES BOTH THE ROUTE AND THE NAV, which is the point: a page that is linked but not
 * served, or served but not linked, cannot happen here because neither is written down twice.
 *
 * `ready` is what makes a page exist. Two of these are finished markup waiting on copy only the
 * owner has — a founding story, an address that receives mail — and until that arrives the page is
 * not in the nav, not in the footer, and its path falls through to the landing. A visitor never
 * meets a page that is waiting for someone; the owner turns each one on by filling in one constant.
 */
interface LandingPage {
  path: string;
  label: string;
  ready: () => boolean;
  render: (p: BandProps) => ReactNode;
}

const PAGES: LandingPage[] = [
  { path: HOW_PATH, label: 'איך זה עובד', ready: () => true, render: (p) => <HowPage {...p} /> },
  /* `render` runs only where `ready` passed, which is what the assertions make safe. */
  { path: STORY_PATH, label: 'הסיפור שלנו', ready: storyReady, render: () => <StoryPage story={STORY!} /> },
  { path: CONTACT_PATH, label: 'יצירת קשר', ready: contactReady, render: () => <ContactPage email={CONTACT_EMAIL} /> },
];

/** The page a path asks for, or null for the landing — including for a path no page claims. */
function pageFor(path: string): LandingPage | null {
  return PAGES.find((page) => page.path === path && page.ready()) ?? null;
}

function Chrome({ ctx, path, children }: { ctx: ModuleScreenContext; path: string; children: ReactNode }) {
  const onLanding = pageFor(path) === null;
  /* A fragment on the page that HAS the form, a path from anywhere else — where a bare `#…` would
     point at nothing and the one control this page may never break would break silently. */
  const login = onLanding ? `#${LOGIN_ID}` : `/#${LOGIN_ID}`;

  const links = PAGES.filter((page) => page.ready()).map((page) => (
    <PageLink key={page.path} to={page.path} ctx={ctx} current={page.path === path}>
      {page.label}
    </PageLink>
  ));

  return (
    <div className="tenders-landing">
      {/*
        The wordmark is RESTORED here, not added. ModuleApp's default login screen passes
        `logo={<HuacaWordmark />}` to LoginCard; overriding `renderLogin` dropped it, which left the
        product's only anonymous page carrying no branding at all.

        It is the PRODUCT's wordmark now, not the platform's. This page showed the huaca pyramid and
        the word "huaca" — a brand the visitor has never heard of, on the one page whose job is to
        introduce מכרזים to a stranger. The comment above still names HuacaWordmark because that is
        genuinely what ModuleApp's default passes; it is simply no longer what this page renders.

        Sticky, because a nav that scrolls away on a page this long is a nav you can use once. The
        hairline is permanent rather than appearing on scroll: over a pale band the bar is the same
        colour as the page behind it, so without a rule the text would slide under an invisible
        edge — and detecting the scroll to draw one is a listener this page does not need.
      */}
      <div className="tenders-topbar">
        <div className="tenders-band-wrap tenders-topbar__in">
          <TendersWordmark />
          <nav className="tenders-topbar__nav" aria-label="ניווט ראשי">
            {/* Marked rather than hidden: a nav that drops the page you are on makes the set of
                pages look different from every other page you were just on. */}
            {links}
            <a className="tenders-topbar__login" href={login}>
              התחברות
            </a>
          </nav>
        </div>
      </div>

      {/* A landmark the page did not have, and the element the column grows: one child that takes
          the slack, so the footer lands at the bottom of a short page instead of mid-screen. */}
      <main className="tenders-main">{children}</main>

      {/*
        `tenders-band-deep` is on the footer for its TOKENS, not only its ground. That class pins the
        design system's text colours to the deep palette; without it the wordmark here would take the
        page's dark ink and disappear into the pine — the exact bug that left the masthead unreadable
        in dark mode, one element further down the page.

        Carries no reveal: a footer that fades in is a footer nobody sees, because it arrives at the
        moment the viewport stops moving.
      */}
      <footer className="tenders-foot tenders-band-deep">
        <div className="tenders-band-wrap tenders-foot__in">
          <TendersWordmark />
          <nav className="tenders-foot__nav" aria-label="ניווט תחתון">
            {links}
            <a href={login}>התחברות</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}

/** The bands of the landing itself. */
function Bands({ ctx, facts }: BandProps) {
  const p: BandProps = { ctx, facts };
  return (
    <>
      {BANDS.map((band, i) => {
        const className = `tenders-band tenders-band-${band.id}${isDeepBand(i) ? ' tenders-band-deep' : ''}`;
        const Art = band.art;
        /*
          The reveal is applied to the STRUCTURE the page already has — the words, the drawing, and
          the full-width block beneath them — rather than to individual sentences. So no band's
          `render` changes, the copy is untouched, and a band added later is revealed by writing it
          rather than by remembering to annotate it.
        */
        const rv = (step: RevealStep) => (band.noReveal ? undefined : revealStep(step));
        /* An `above` takes the first slot, so everything under it moves one place down the
           sequence and the band still arrives top to bottom. */
        const after = band.above ? 1 : 0;
        const slot = (step: 1 | 2 | 3) => rv((step + after) as RevealStep);
        const inner = (
          <div className="tenders-band-wrap">
            {band.above ? <div className={rv(1)}>{band.above(p)}</div> : null}
            {band.artLayout === 'strip' ? (
              <>
                <div className={slot(1)}>{band.render(p)}</div>
                {/* The class goes ON the drawing: `.tenders-artrow > .tenders-art` and the strip
                    sizing both select it directly, so a wrapper would silently drop its width. */}
                <Art className={`tenders-art-strip${slot(2) ? ` ${slot(2)}` : ''}`} />
              </>
            ) : (
              <div className="tenders-artrow">
                <div className={slot(1)}>{band.render(p)}</div>
                <Art className={slot(2)} />
              </div>
            )}
            {band.below ? <div className={slot(3)}>{band.below(p)}</div> : null}
          </div>
        );
        return band.masthead ? (
          <header key={band.id} className={className}>
            {inner}
          </header>
        ) : (
          <section key={band.id} className={className}>
            {inner}
          </section>
        );
      })}
    </>
  );
}

/**
 * /how-it-works — the same four gates, at length.
 *
 * TWO BANDS, so the grounds still alternate. The head and the chapters share one pale band rather
 * than taking one each: hand-setting a ground per section on a page this short is exactly how two
 * pale bands end up adjacent, which is the failure the landing's own alternation exists to prevent.
 */
function HowPage({ ctx, facts }: BandProps) {
  return (
    <>
      <section className="tenders-band tenders-band-how">
        <div className="tenders-band-wrap">
          <div className="tenders-artrow">
            <div className={revealStep(1)}>
              <p className="tenders-landing-eyebrow">איך זה עובד</p>
              <h1>מהפרסום ועד אליך.</h1>
              <p className="tenders-landing-lede">
                הגרסה המלאה של ארבעת השערים. מה שכתוב כאן הוא מה שרץ היום — לא מה שמתוכנן.
              </p>
            </div>
            <PulledApart className={revealStep(2)} />
          </div>
          <div className={revealStep(3)}>
            <GateChapters overview={facts.overview} />
          </div>
        </div>
      </section>

      <section className="tenders-band tenders-band-deep tenders-how__close">
        <div className="tenders-band-wrap">
          <h2>רוצה לראות את זה על הלוח האמיתי?</h2>
          {/*
            A path, not a fragment: the form is on the landing, not here. Left as a plain anchor on
            purpose — this is the route to sign in, and it works with the script dead.
          */}
          <a className="tenders-landing-cta" href={`/#${LOGIN_ID}`}>
            תפתח את הלוח
          </a>
          <TrialLine facts={facts} />
          <p className="tenders-landing-note">
            <PageLink to="/" ctx={ctx}>
              חזרה לעמוד הראשי
            </PageLink>
          </p>
        </div>
      </section>
    </>
  );
}

/**
 * The module's anonymous surface — every page of it.
 *
 * ONE COMPONENT, SEVERAL PAGES, AND NO PLATFORM CHANGE. `ModuleApp` hands `renderLogin` every path
 * an anonymous visitor asks for, except the three it consumes itself (`/auth/magic`, `/invite`,
 * `/auth/callback`), and firebase.json already rewrites `**` to index.html. So a second page is a
 * branch here rather than a route registered anywhere: the platform still decides WHEN this screen
 * shows, and this file decides what it looks like — exactly the contract the slot is for.
 *
 * ANYTHING UNRECOGNISED IS THE LANDING, deliberately. A visitor who mistypes a path, or follows a
 * link to a page that no longer exists, gets the product's front page and a working sign-in form
 * rather than a 404 rendered by a login screen.
 */
export function Landing({ ctx }: { ctx: ModuleScreenContext }) {
  const facts = useLandingFacts();
  const { path } = useRoute();

  /*
    The platform centres its auth screens in a 400px column, which is right for a card and wrong for
    full-bleed bands. Marking the document rather than reaching up the tree is what lets landing.css
    undo that from this app's own stylesheet, with no change to @huaca/ui and no effect on the three
    other modules that still want the column. Removed on unmount, so signing in restores it.

    LAYOUT EFFECT, NOT A PASSIVE ONE, and that is the whole point of the hook here. A `useEffect`
    runs AFTER the browser has painted, so the first painted frame of this page was the entire
    marketing site squeezed into the 400px column — measured on staging at 10839px tall against a
    settled 5319px. The scrollbar rendered a thumb for a document twice the real length and then
    snapped, and a reload while scrolled restored the old offset against the wrong height. A layout
    effect runs synchronously after commit and before paint, so that frame is never shown.
  */
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.add(LANDING_ROOT_CLASS);
    return () => root.classList.remove(LANDING_ROOT_CLASS);
  }, []);

  /* Keyed on the path: a client-side navigation replaces every unit in the DOM, and a reveal armed
     for the old ones leaves the new page hidden. See landing-reveal.ts. */
  useReveal(path);

  return (
    <Chrome ctx={ctx} path={path}>
      {pageFor(path)?.render({ ctx, facts }) ?? <Bands ctx={ctx} facts={facts} />}
    </Chrome>
  );
}
